// src/components/MemberRenewContainer/HouseholdHead.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { formatDate } from 'lib/formatters/date';
import CardIdInput from './CardIdInput';
import { useUpdateMemberMutation } from 'store/api/baseApiSlice';

const DEFAULT_PROFILE_IMAGE = 'https://static.thenounproject.com/png/user-default-icon-4154905-512.png';

/**
 * DetailItem Component - Reusable component for displaying member information
 */
const DetailItem = ({ label, value, icon }) => {
  const getIcon = () => {
    const iconClasses = "tw-w-3 tw-h-3 tw-text-gray-400 tw-mr-1.5 tw-mt-0.5";
    switch (icon) {
      case 'user':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'calendar':
      case 'calendar-alt':
      case 'calendar-check':
      case 'calendar-times':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'id-card':
      case 'id-badge':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
          </svg>
        );
      case 'map-marker':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case 'phone':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        );
      case 'briefcase':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'tag':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        );
      case 'sync':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'gender':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="tw-bg-gray-50 tw-p-1.5 tw-rounded tw-border tw-border-gray-200 hover:tw-bg-gray-100 tw-transition-colors tw-duration-200">
      <div className="tw-flex tw-items-start">
        {getIcon()}
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-text-[9px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-0.5">
            {label}
          </div>
          <div className="tw-text-[11px] tw-font-medium tw-text-gray-800 tw-break-words">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * HouseholdHead Component
 * Displays comprehensive information about the household head.
 */
/**
 * Compact QR Code Input Component
 */
const QRCodeInput = ({ initialQrCode, onQrCodeUpdate }) => {
  const [inputValue, setInputValue] = useState(initialQrCode || '');
  const [error, setError] = useState('');

  const formatWithSpaces = (value) => {
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    const parts = [];
    for (let i = 0; i < cleaned.length; i += 3) {
      parts.push(cleaned.substring(i, i + 3));
    }
    return parts.join(' ');
  };

  const removeSpaces = (value) => {
    return value.replace(/\s+/g, '');
  };

  const validateQrCode = (value) => {
    const regex = /^[A-Z]{3}\d{6}$/;
    return regex.test(value);
  };

  const handleChange = (e) => {
    const formatted = formatWithSpaces(e.target.value);
    setInputValue(formatted);
    setError('');
  };

  const handleBlur = () => {
    const unformatted = removeSpaces(inputValue);
    if (unformatted && unformatted.length !== 9) {
      setError('QR Code must be exactly 9 characters.');
      return;
    }
    if (unformatted && !validateQrCode(unformatted)) {
      setError('Invalid format. Use 3 letters + 6 digits.');
      return;
    }

    if (unformatted) {
      onQrCodeUpdate(unformatted)
        .then(() => {
          setError('');
        })
        .catch((err) => {
          setError('Update failed.');
          console.error(err);
        });
    }
  };

  return (
    <div className="tw-bg-gray-50 tw-p-1.5 tw-rounded tw-border tw-border-gray-200">
      <div className="tw-flex tw-items-start">
        <svg className="tw-w-3 tw-h-3 tw-text-gray-400 tw-mr-1.5 tw-mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
        </svg>
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-text-[9px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-0.5">
            QR Code
          </div>
          <input
            type="text"
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={11}
            className={`tw-w-full tw-text-[11px] tw-font-medium tw-text-gray-800 tw-px-1.5 tw-py-0.5 tw-rounded tw-border ${
              error ? 'tw-border-red-300 tw-bg-red-50' : 'tw-border-gray-300 tw-bg-white'
            } focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500`}
            placeholder="AAA 123 456"
          />
          {error && (
            <p className="tw-text-[9px] tw-text-red-600 tw-mt-0.5">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const HouseholdHead = ({ householdHead }) => {
  const [cardId, setCardId] = useState(householdHead?.cardId || '');
  const [qrCode, setQrCode] = useState(householdHead?.qrCode || '');
  const [updateMember] = useUpdateMemberMutation();
  

   /**
   * Handles the update of Card ID by calling the backend API.
   * @param {string} newCardId - The validated and formatted Card ID.
   * @returns {Promise<void>}
   */
   const handleCardIdUpdate = async (newCardId) => {
    try {
      const formData = new FormData();
      formData.append('id', householdHead.id);
      formData.append('card_id', newCardId);
      const response = await updateMember(formData)
      if (response.data.error) {
        throw new Error('Card ID has already been taken or Invalid');
      }
    } catch (error) {
      console.error('Error updating Card ID:', error);
      throw error;
    }
  };

  /**
   * Handles the update of QR Code by calling the backend API.
   * @param {string} newQrCode - The validated and formatted QR Code.
   * @returns {Promise<void>}
   */
  const handleQrCodeUpdate = async (newQrCode) => {
    try {
      const formData = new FormData();
      formData.append('id', householdHead.id);
      formData.append('qr_code', newQrCode);
      const response = await updateMember(formData)
      if (response.data.error) {
        throw new Error('QR Code has already been taken or Invalid');
      }
    } catch (error) {
      console.error('Error updating QR Code:', error);
      throw error;
    }
  };

  if (!householdHead) {
    return (
      <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded-lg tw-mb-6 tw-shadow-sm">
        <div className="tw-flex tw-items-center">
          <svg className="tw-w-5 tw-h-5 tw-mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="tw-font-medium">Household head not found.</span>
        </div>
      </div>
    );
  }

  const {
    photoUrl,
    fullName,
    age,
    gender,
    birthdate,
    membershipNumber,
    localMembershipNumber,
    memberAddress,
    phoneNumber,
    profession,
    membershipType,
    enrolledAt,
    status,
    coverageEndDate,
    renewedAt,
    needsRenewal,
    unpaid,
    absentee,
    isCardInvalid,
  } = householdHead;

  const profileImageUrl = photoUrl
    ? `${process.env.REACT_APP_COVERAGE_API_BASE}${photoUrl}`
    : DEFAULT_PROFILE_IMAGE;

  /**
   * Determine Membership Status Badge
   */
  const getMembershipBadge = () => {
    if (!coverageEndDate) return null;

    const today = new Date();
    const endDate = new Date(coverageEndDate);

    if (endDate >= today) {
      // Membership is active
      return (
        <span
          className="tw-inline-flex tw-items-center tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-gradient-to-r tw-from-green-100 tw-to-emerald-100 tw-text-green-800 tw-border tw-border-green-300 tw-shadow-sm"
          aria-label="Membership Active"
        >
          <svg className="tw-w-3 tw-h-3 tw-mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Active
        </span>
      );
    } else {
      // Membership has expired
      return (
        <span
          className="tw-inline-flex tw-items-center tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-gradient-to-r tw-from-red-100 tw-to-rose-100 tw-text-red-800 tw-border tw-border-red-300 tw-shadow-sm"
          aria-label="Membership Expired"
        >
          <svg className="tw-w-3 tw-h-3 tw-mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          Expired
        </span>
      );
    }
  };

  const membershipBadge = getMembershipBadge();

  return (
    <div className="tw-bg-white tw-shadow-lg tw-rounded-lg tw-p-3 tw-mb-4 tw-border tw-border-gray-200 tw-overflow-hidden">
      {/* Header Section */}
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3 tw-pb-2 tw-border-b tw-border-gray-200">
        <div className="tw-flex tw-items-center">
          <div className="tw-bg-blue-100 tw-p-1.5 tw-rounded tw-mr-2">
            <svg className="tw-w-4 tw-h-4 tw-text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="tw-text-lg tw-font-bold tw-text-gray-800">
            Household Head
          </h2>
        </div>
        {membershipBadge}
      </div>

      <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-start tw-gap-4">
        {/* Profile Image Section */}
        <div className="tw-flex-shrink-0 tw-flex tw-flex-col tw-items-center">
          <div className="tw-relative">
            <img
              src={profileImageUrl}
              alt={fullName}
              className="tw-w-24 tw-h-24 tw-rounded-full tw-object-cover tw-shadow-md tw-border-2 tw-border-gray-100"
            />
            <div className="tw-absolute tw-bottom-0 tw-right-0 tw-bg-blue-500 tw-rounded-full tw-p-1 tw-shadow-md tw-border tw-border-white">
              <svg className="tw-w-3 tw-h-3 tw-text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <div className="tw-mt-2 tw-text-center">
            <h3 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mb-0.5">{fullName}</h3>
            <p className="tw-text-xs tw-text-gray-500">{profession || 'N/A'}</p>
          </div>
        </div>

        {/* Member Details Section */}
        <div className="tw-flex-1 tw-w-full">
          <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-2">
            <DetailItem label="Full Name" value={fullName} icon="user" />
            <DetailItem label="Age" value={age} icon="calendar" />
            <DetailItem 
              label="Gender" 
              value={gender === 'F' ? 'Female' : gender === 'M' ? 'Male' : 'Other'} 
              icon="gender" 
            />
            <DetailItem label="Birthdate" value={formatDate(birthdate)} icon="calendar-alt" />
            <DetailItem label="Membership Number" value={membershipNumber} icon="id-card" />
            <DetailItem label="Local Membership Number" value={localMembershipNumber} icon="id-badge" />
            <DetailItem label="Address" value={memberAddress || 'N/A'} icon="map-marker" />
            <DetailItem label="Phone Number" value={phoneNumber || 'N/A'} icon="phone" />
            <DetailItem label="Profession" value={profession || 'N/A'} icon="briefcase" />
            <DetailItem label="Membership Type" value={membershipType || 'N/A'} icon="tag" />
            <DetailItem label="Enrollment Date" value={formatDate(enrolledAt)} icon="calendar-check" />
            <DetailItem 
              label="Coverage End Date" 
              value={coverageEndDate ? formatDate(coverageEndDate) : 'N/A'} 
              icon="calendar-times" 
            />
            <DetailItem label="Renewed At" value={renewedAt ? formatDate(renewedAt) : 'N/A'} icon="sync" />
            <QRCodeInput 
              initialQrCode={qrCode}
              onQrCodeUpdate={handleQrCodeUpdate}
            />
          </div>

          
        </div>
      </div>
    </div>
  );
};

HouseholdHead.propTypes = {
  householdHead: PropTypes.shape({
    photoUrl: PropTypes.string,
    fullName: PropTypes.string.isRequired,
    age: PropTypes.number.isRequired,
    gender: PropTypes.string.isRequired,
    birthdate: PropTypes.string.isRequired,
    membershipNumber: PropTypes.string.isRequired,
    localMembershipNumber: PropTypes.string.isRequired,
    memberAddress: PropTypes.string,
    phoneNumber: PropTypes.string,
    profession: PropTypes.string,
    membershipType: PropTypes.string,
    enrolledAt: PropTypes.string.isRequired,
    status: PropTypes.string,
    coverageEndDate: PropTypes.string,
    renewedAt: PropTypes.string,
    needsRenewal: PropTypes.bool,
    unpaid: PropTypes.bool,
    absentee: PropTypes.bool,
    isCardInvalid: PropTypes.bool,
  }),
};

export default HouseholdHead;
